// store/thunks/orderThunks.js - FIXED VERSION
// Changes:
// 1. Removed duplicate admin fulfillment thunks
// 2. Kept only the original fulfillment thunks (admin can use these)
// 3. Verified all endpoint paths

import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// ============================================================================
// ADMIN THUNKS - ORDER CREATION
// ============================================================================

export const createOrderFromSessionThunk = createAsyncThunk(
  "orders/admin/createFromSession",
  async (sessionData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        "/orders/admin/create-from-session",
        sessionData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createOrderFromCartThunk = createAsyncThunk(
  "orders/admin/createFromCart",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        "/orders/admin/create-from-cart",
        orderData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createDirectOrderThunk = createAsyncThunk(
  "orders/admin/createDirect",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await api.post("/orders/admin/create-direct", orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// ADMIN THUNKS - ORDER EDITING
// ============================================================================

export const getOrderDraftThunk = createAsyncThunk(
  "orders/admin/getDraft",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/orders/admin/order/${orderId}/draft`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateOrderItemsThunk = createAsyncThunk(
  "orders/admin/updateItems",
  async ({ orderId, action, itemData }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/orders/admin/order/${orderId}/items`, {
        action,
        itemData,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// ADMIN THUNKS - PRICING & ADJUSTMENTS
// ============================================================================

export const calculateOrderPricingThunk = createAsyncThunk(
  "orders/admin/calculatePricing",
  async ({ orderId, recalculate = false }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/orders/admin/order/${orderId}/pricing`, {
        params: { recalculate },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const addPriceAdjustmentThunk = createAsyncThunk(
  "orders/admin/addPriceAdjustment",
  async ({ orderId, adjustmentData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/pricing/adjustments`,
        adjustmentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const removePriceAdjustmentThunk = createAsyncThunk(
  "orders/admin/removePriceAdjustment",
  async (
    { orderId, adjustmentId, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.delete(
        `/orders/admin/order/${orderId}/pricing/adjustments/${adjustmentId}`,
        { params: { adminCode, adminName } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const approvePriceAdjustmentThunk = createAsyncThunk(
  "orders/admin/approvePriceAdjustment",
  async (
    { orderId, adjustmentId, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/pricing/adjustments/${adjustmentId}/approve`,
        { adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const rejectPriceAdjustmentThunk = createAsyncThunk(
  "orders/admin/rejectPriceAdjustment",
  async (
    { orderId, adjustmentId, rejectionReason, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/pricing/adjustments/${adjustmentId}/reject`,
        { rejectionReason, adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const lockPricesThunk = createAsyncThunk(
  "orders/admin/lockPrices",
  async (
    { orderId, lockDurationMinutes = 30, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/pricing/lock`,
        { lockDurationMinutes, adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error?.error || error.message
      );
    }
  }
);

export const unlockPricesThunk = createAsyncThunk(
  "orders/admin/unlockPrices",
  async ({ orderId, adminCode, adminName }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/pricing/unlock`,
        { adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// ADMIN THUNKS - EXCHANGE ITEMS
// ============================================================================

export const calculateExchangeValueThunk = createAsyncThunk(
  "orders/admin/calculateExchangeValue",
  async (
    {
      branchCode,
      itemType,
      grossWeight,
      netWeight,
      stoneWeight,
      metalDetails,
      conditionGrade,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        "/orders/admin/exchange/calculate-value",
        {
          branchCode,
          itemType,
          grossWeight,
          netWeight,
          stoneWeight,
          metalDetails,
          conditionGrade,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const addExchangeItemThunk = createAsyncThunk(
  "orders/admin/addExchangeItem",
  async ({ orderId, exchangeData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/exchange/items`,
        exchangeData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const editExchangeItemThunk = createAsyncThunk(
  "orders/admin/editExchangeItem",
  async ({ orderId, exchangeItemId, updateData }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/orders/admin/order/${orderId}/exchange/items/${exchangeItemId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const adjustExchangeValueThunk = createAsyncThunk(
  "orders/admin/adjustExchangeValue",
  async (
    { orderId, exchangeItemId, adjustedValue, reason, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        `/orders/admin/order/${orderId}/exchange/items/${exchangeItemId}/value`,
        { adjustedValue, reason, adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const acceptExchangeItemThunk = createAsyncThunk(
  "orders/admin/acceptExchangeItem",
  async (
    { orderId, exchangeItemId, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/exchange/items/${exchangeItemId}/accept`,
        { adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const unacceptExchangeItemThunk = createAsyncThunk(
  "orders/admin/unacceptExchangeItem",
  async (
    { orderId, exchangeItemId, reason, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/exchange/items/${exchangeItemId}/unaccept`,
        { reason, adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const removeExchangeItemThunk = createAsyncThunk(
  "orders/admin/removeExchangeItem",
  async (
    { orderId, exchangeItemId, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.delete(
        `/orders/admin/order/${orderId}/exchange/items/${exchangeItemId}`,
        { params: { adminCode, adminName } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// ADMIN THUNKS - ORDER NOTES
// ============================================================================

export const updateOrderNotesThunk = createAsyncThunk(
  "orders/admin/updateNotes",
  async (
    { orderId, adminNotes, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(`/orders/admin/order/${orderId}/notes`, {
        adminNotes,
        adminCode,
        adminName,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// ADMIN THUNKS - APPROVAL WORKFLOW
// ============================================================================

export const submitForApprovalThunk = createAsyncThunk(
  "orders/admin/submitApproval",
  async ({ orderId, notes }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/submit-approval`,
        { notes }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const approveOrderThunk = createAsyncThunk(
  "orders/admin/approve",
  async ({ orderId, notes }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/approve`,
        { notes }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const rejectOrderThunk = createAsyncThunk(
  "orders/admin/reject",
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/orders/admin/order/${orderId}/reject`, {
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const finalizeOrderThunk = createAsyncThunk(
  "orders/admin/finalize",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/finalize`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// ADMIN THUNKS - ORDER MANAGEMENT
// ============================================================================

export const getAllOrdersForAdminThunk = createAsyncThunk(
  "orders/admin/getAll",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/orders/admin/orders", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getOrderByIdForAdminThunk = createAsyncThunk(
  "orders/admin/getById",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/orders/admin/order/${orderId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// src/store/thunks/orderThunks.js

export const updateOrderStatusThunk = createAsyncThunk(
  "orders/admin/updateStatus",
  async ({ orderId, status, comment, refundDetails }, { rejectWithValue }) => {
    try {
      const payload = {
        status,
        comment,
      };

      // ✅ Include refund details if provided
      if (refundDetails) {
        payload.refundDetails = refundDetails;
      }

      const response = await api.put(
        `/orders/admin/order/${orderId}/status`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updatePaymentStatusThunk = createAsyncThunk(
  "orders/admin/updatePayment",
  async ({ orderId, paymentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/orders/admin/order/${orderId}/payment`,
        paymentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getOrderStatsThunk = createAsyncThunk(
  "orders/admin/getStats",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/orders/admin/stats", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const cancelOrderThunk = createAsyncThunk(
  "orders/admin/cancel",
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/orders/admin/order/${orderId}/cancel`, {
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// FULFILLMENT THUNKS - ADMIN (No duplicates!)
// ============================================================================

export const schedulePickupThunk = createAsyncThunk(
  "orders/admin/schedulePickup",
  async ({ orderId, scheduledDate, timeSlot, notes }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/schedule-pickup`,
        { scheduledDate, timeSlot, notes }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const reschedulePickupThunk = createAsyncThunk(
  "orders/admin/reschedulePickup",
  async ({ orderId, newDate, timeSlot, reason }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/orders/admin/order/${orderId}/reschedule-pickup`,
        { newDate, timeSlot, reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const scheduleDeliveryThunk = createAsyncThunk(
  "orders/admin/scheduleDelivery",
  async ({ orderId, scheduledDate, timeSlot, notes }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/schedule-delivery`,
        { scheduledDate, timeSlot, notes }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const rescheduleDeliveryThunk = createAsyncThunk(
  "orders/admin/rescheduleDelivery",
  async ({ orderId, newDate, timeSlot, reason }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/orders/admin/order/${orderId}/reschedule-delivery`,
        { newDate, timeSlot, reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getFulfillmentDetailsThunk = createAsyncThunk(
  "orders/admin/getFulfillmentDetails",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/orders/admin/order/${orderId}/fulfillment`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const markReadyForPickupThunk = createAsyncThunk(
  "orders/admin/markReadyForPickup",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/ready-for-pickup`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const completePickupThunk = createAsyncThunk(
  "orders/admin/completePickup",
  async ({ orderId, pickedUpBy }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/complete-pickup`,
        { pickedUpBy }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const recordDeliveryAttemptThunk = createAsyncThunk(
  "orders/admin/recordDeliveryAttempt",
  async ({ orderId, status, reason, nextAttemptDate }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/delivery-attempt`,
        { status, reason, nextAttemptDate }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const completeDeliveryThunk = createAsyncThunk(
  "orders/admin/completeDelivery",
  async (
    { orderId, receivedBy, signature, proofOfDelivery },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/complete-delivery`,
        { receivedBy, signature, proofOfDelivery }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// ADMIN THUNKS - REFUND MANAGEMENT
// ============================================================================

/**
 * Initiate refund for excess exchange value
 * POST /orders/admin/order/:orderId/refund/initiate
 */
export const initiateRefundThunk = createAsyncThunk(
  "orders/admin/initiateRefund",
  async (
    { orderId, refundMethod, bankDetails, upiDetails, notes },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/refund/initiate`,
        {
          refundMethod,
          bankDetails,
          upiDetails,
          notes,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Approve refund (for large amounts)
 * POST /orders/admin/order/:orderId/refund/approve
 */
export const approveRefundThunk = createAsyncThunk(
  "orders/approveRefund",
  async ({ orderId, approvalNotes, internalNotes }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `orders/admin/order/${orderId}/refund/approve`,
        {
          approvalNotes,
          internalNotes,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve refund"
      );
    }
  }
);

export const rejectRefundThunk = createAsyncThunk(
  "orders/rejectRefund",
  async ({ orderId, reason, alternativeAction }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `orders/admin/order/${orderId}/refund/reject`,
        {
          reason,
          alternativeAction,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject refund"
      );
    }
  }
);

/**
 * Complete refund processing
 * POST /orders/admin/order/:orderId/refund/complete
 */
export const completeRefundThunk = createAsyncThunk(
  "orders/admin/completeRefund",
  async (
    { orderId, transactionId, referenceNumber, bankDetails, upiDetails, notes },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/refund/complete`,
        {
          transactionId,
          referenceNumber,
          bankDetails,
          upiDetails,
          notes,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Get refund details for an order
 * GET /orders/admin/order/:orderId/refund
 */
export const getRefundDetailsThunk = createAsyncThunk(
  "orders/admin/getRefundDetails",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/orders/admin/order/${orderId}/refund`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Get all orders with pending refunds
 * GET /orders/admin/orders/refunds-pending
 */
export const getPendingRefundsThunk = createAsyncThunk(
  "orders/admin/getPendingRefunds",
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/orders/admin/orders/refunds-pending", {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Cancel a pending refund
 * POST /orders/admin/order/:orderId/refund/cancel
 */
export const cancelRefundThunk = createAsyncThunk(
  "orders/admin/cancelRefund",
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/refund/cancel`,
        { reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// KARIGAR JOB MANAGEMENT THUNKS (from Order context)
// ============================================================================

/**
 * Get all karigar jobs linked to an order
 */
export const getOrderKarigarJobsThunk = createAsyncThunk(
  "orders/admin/getKarigarJobs",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/orders/admin/order/${orderId}/karigar-jobs`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Create karigar job for a specific order item
 */
export const createOrderKarigarJobThunk = createAsyncThunk(
  "orders/admin/createKarigarJob",
  async ({ orderId, jobData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/karigar-jobs`,
        jobData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Create karigar jobs for all order items (bulk)
 */
export const createBulkOrderKarigarJobsThunk = createAsyncThunk(
  "orders/admin/createBulkKarigarJobs",
  async ({ orderId, bulkData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/karigar-jobs/bulk`,
        bulkData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Update karigar job from order context
 */
export const updateOrderKarigarJobThunk = createAsyncThunk(
  "orders/admin/updateKarigarJob",
  async ({ orderId, jobId, updateData }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/orders/admin/order/${orderId}/karigar-jobs/${jobId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Cancel karigar job from order context
 */
export const cancelOrderKarigarJobThunk = createAsyncThunk(
  "orders/admin/cancelKarigarJob",
  async ({ orderId, jobId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/orders/admin/order/${orderId}/karigar-jobs/${jobId}`,
        { data: { reason } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Get karigar work summary for order
 */
export const getOrderKarigarSummaryThunk = createAsyncThunk(
  "orders/admin/getKarigarSummary",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/orders/admin/order/${orderId}/karigar-summary`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Check karigar readiness before fulfillment
 */
export const checkKarigarReadinessThunk = createAsyncThunk(
  "orders/admin/checkKarigarReadiness",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/orders/admin/order/${orderId}/karigar-readiness`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Force complete all pending karigar jobs
 */
export const forceCompleteKarigarJobsThunk = createAsyncThunk(
  "orders/admin/forceCompleteKarigarJobs",
  async ({ orderId, completionData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/orders/admin/order/${orderId}/complete-karigar-jobs`,
        completionData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ============================================================================
// ACTIONS
// ============================================================================
export const clearExchangeCalculation = createAction(
  "orders/clearExchangeCalculation"
);
export const clearFulfillmentDetails = createAction(
  "orders/clearFulfillmentDetails"
);
export const setFulfillmentType = createAction("orders/setFulfillmentType");
export const clearOrderError = createAction("orders/clearOrderError");
export const clearCurrentOrder = createAction("orders/clearCurrentOrder");
export const clearDraftOrder = createAction("orders/clearDraftOrder");
export const clearPricingData = createAction("orders/clearPricingData");
export const clearAdjustments = createAction("orders/clearAdjustments");
export const clearRefundDetails = createAction("orders/clearRefundDetails");
export const clearPendingRefunds = createAction("orders/clearPendingRefunds");

// KARIGAR UI ACTIONS
export const openKarigarAssignmentModal = createAction(
  "orders/openKarigarAssignmentModal"
);
export const closeKarigarAssignmentModal = createAction(
  "orders/closeKarigarAssignmentModal"
);
export const openKarigarStatusPanel = createAction(
  "orders/openKarigarStatusPanel"
);
export const closeKarigarStatusPanel = createAction(
  "orders/closeKarigarStatusPanel"
);
export const openKarigarQueueModal = createAction(
  "orders/openKarigarQueueModal"
);
export const closeKarigarQueueModal = createAction(
  "orders/closeKarigarQueueModal"
);
export const clearKarigarUI = createAction("orders/clearKarigarUI");
